<script context="module" lang="ts">
  import { analytics, toggleBodyScroll } from '$lib/utils';
  import { writable } from 'svelte/store';

  let isVisible = writable(false);
  let focusOnClose: HTMLElement | Element | null = null;
  let selectedTab = 'all';

  /**
   * @param {object} track - tracking object
   * @param {HTMLElement} refocus - element to focus after modal is closed
   */
  export function open(track = null, refocus: HTMLElement | Element | null = null, selectedFilter = 'all') {
    isVisible.set(true);
    if (track) {
      analytics.track(track);
    }
    focusOnClose = !refocus && document.activeElement ? document.activeElement : refocus;
    selectedTab = selectedFilter;
  }
</script>

<script lang="ts">
  import { page } from '$app/stores';
  import type { Api_Game } from '$lib/types/api/graphql';
  import { fly } from 'svelte/transition';
  import { searchIndex } from '$lib/utils';

  import SearchInput from '$components/search/SearchInput.svelte';
  import SearchTabsResultsContainer from './search/SearchTabsResultsContainer.svelte';
  import { clickOutsideAction } from '$lib/directives/clickOutsideAction';

  export let activeGame: Api_Game | null = null;
  export let selectedFilter = 'all';

  $: currentGame = activeGame;

  let query = '';
  let previousQuery = null;
  let inputFocus;
  let isFetching = false;
  let results = {};
  let isSearched = false;
  let showAllResults = true;
  let selectedKey = '';

  let prevPath = $page.url.pathname;

  $: indexFilterMap = {
    accounts: `games.id = ${currentGame?.id}`,
    groups: `game_id = ${currentGame?.id}`,
    games: [],
    product_guides: `game_id = ${currentGame?.id}`,
    product_coaching: `game_id = ${currentGame?.id}`,
    product_events: `game_id = ${currentGame?.id}`
  };

  let indices = [
    {
      indexName: 'games',
      params: { hitsPerPage: 3, attributesToHighlight: ['title.en'] }
    },
    {
      indexName: 'accounts',
      params: {
        hitsPerPage: 3,
        attributesToHighlight: ['name', 'game_titles'],
        filter: '(is_core = true OR is_pro = true)'
      }
    },
    {
      indexName: 'groups',
      params: { hitsPerPage: 3, attributesToHighlight: ['title'], sort: ['member_count:desc'] }
    },
    {
      indexName: 'product_guides',
      params: { hitsPerPage: 3, attributesToHighlight: ['title', 'seller_name'], sort: [] }
    },
    {
      indexName: 'product_coaching',
      params: { hitsPerPage: 3, attributesToHighlight: ['name', 'seller_name'], sort: [] }
    },
    {
      indexName: 'product_events',
      params: {
        hitsPerPage: 3,
        filter: '(offer_replay_purchase = true OR is_complete = false)',
        attributesToHighlight: ['name', 'seller_name'],
        sort: []
      }
    }
  ];

  function doSearch(indices) {
    if (selectedKey) return;
    isFetching = true;
    previousQuery = query;

    return searchIndex(
      currentGame
        ? indices.map(idx => ({
            ...idx,
            params: {
              ...idx.params,
              filter: `${idx.params.filter || ''} ${
                idx.params.filter?.length > 0 && indexFilterMap[idx.indexName]?.length > 0 ? 'AND' : ''
              } ${indexFilterMap[idx.indexName]}`
            }
          }))
        : indices,
      query,
      {}
    )
      .then(data => {
        isSearched = true;
        if (data?.results) {
          indices.map(({ indexName }, i) => (results[indexName] = { indexName, ...data.results[i] }));
        }
      })
      .catch(error => {
        // ...
      })
      .finally(() => (isFetching = false));
  }

  function filterByIndex(indexName) {
    // create a new set of indices with the updated hitsPerPage since we want to show only the filtered category!
    indices.forEach(idx => {
      if (idx.indexName === indexName) {
        // 18 can be changed in the fututre
        idx.params.hitsPerPage = 18;
      } else {
        // 3 from each index if 'all' filter selected since it's the initial value
        // 0 if the index not selected and not the filter isn't 'all'
        idx.params.hitsPerPage = indexName === 'all' ? 3 : 0;
      }
    });
  }

  $: if ($page.url.pathname !== prevPath) {
    $isVisible = false;
  }

  async function onShowSearch() {
    if (selectedTab !== 'all') {
      selectedFilter = selectedTab;
      filterByIndex(selectedFilter);
      await doSearch(indices);
    } else {
      // Don't execute query if it is the same as last time.
      if (query !== previousQuery) {
        await doSearch(indices);
      }
    }
    if (inputFocus) {
      setTimeout(() => {
        inputFocus();
      }, 100);
    }
  }

  async function onHideSearch() {
    query = '';
    $isVisible = false;
    currentGame = activeGame;
    if (focusOnClose) {
      focusOnClose.focus();
    }
  }

  $: if ($isVisible) {
    onShowSearch();
  }
  $: $isVisible, toggleBodyScroll(!$isVisible);
</script>

{#if $isVisible}
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div
    role="dialog"
    class="search-container z-higher-modal bg-ondark-low md:bg-ondark-overlay animate-fade-in from-ondark-low/90 fixed inset-0 bg-gradient-to-b from-20% to-transparent backdrop-blur"
    in:fly={{ y: -50, duration: 300 }}
    out:fly={{ y: 50, duration: 300 }}
    on:keyup={e => {
      if (e.key === 'Escape') {
        onHideSearch();
      }
    }}
  >
    <div class="h-full">
      <div
        class="mx-2 flex h-full min-h-64 max-w-7xl flex-col pb-3 md:mx-auto md:max-w-[980px]"
        use:clickOutsideAction
        on:clickoutside={$isVisible && onHideSearch}
      >
        <SearchInput
          bind:query
          bind:inputFocus
          bind:selectedFilter
          on:search={() => doSearch(indices)}
          on:close={onHideSearch}
          on:removeGame={() => {
            currentGame = undefined;
            showAllResults = true;
            selectedKey = '';
            doSearch(indices);
          }}
          on:filterByIndex={async ({ detail: { indexName } }) => {
            // search with the new indices
            filterByIndex(indexName);
            await doSearch(indices);
          }}
          on:clickAll={({ detail }) => ((showAllResults = false), (selectedKey = detail))}
          showFilteredResults={!showAllResults && !!selectedKey}
          {currentGame}
          {isSearched}
          {results}
          {isFetching}
          {previousQuery}
          isFloating={!(!showAllResults && selectedKey)}
          placeholder={currentGame ? `Search within ${currentGame.localizedTitles.en}` : undefined}
          alwaysShowClearButton
        />
        {#if !showAllResults && selectedKey}
          <div class="flex shrink grow-0 flex-col overflow-hidden">
            <SearchTabsResultsContainer
              {selectedKey}
              {query}
              {currentGame}
              on:back={() => ((showAllResults = true), (selectedKey = ''), doSearch(indices))}
            />
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}
